import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorText } from '../lib/api';
import { statusWord, useRunScope } from '../lib/characters';
import { when } from '../lib/datetime';
import { useDraft } from '../lib/draft';
import { effectAsParsed } from '../lib/effects/defaults';
import { MODIFIER_REGISTRY } from '../lib/effects/types';
import {
  applyEffect,
  endEffect,
  explainEffect,
  playerEffects,
  stepText,
  type EffectExplain,
} from '../lib/effectsApi';

const th = 'px-2 py-1 text-left';
const td = 'px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const input = 'rounded border-2 border-ink bg-white px-2 py-0.5 text-sm';

/**
 * A player's Effects (ADR 0045, card 9's admin endpoints): the run's active view per member, the instances (live first, then history)
 * with End, zone presence and their zones, "Why is X?" (the evaluator's explain trace for one member and key), and Apply. Apply and
 * End need a reason and a confirm(); the API audits both and enforces the Admin role.
 */
export function PlayerEffectsSection({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const qc = useQueryClient();
  const draft = useDraft();
  const key = ['admin-player-effects', playerId, runId];
  const q = useQuery({ queryKey: key, queryFn: () => playerEffects(client, playerId) });
  const [reason, setReason] = useState('');
  const [effectId, setEffectId] = useState('');
  const [target, setTarget] = useState('');
  const [member, setMember] = useState('player');
  const [modKey, setModKey] = useState('stat.nrv');
  const [explain, setExplain] = useState<EffectExplain | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const done = (data: unknown) => {
    qc.setQueryData(key, data);
    setProblem(null);
  };
  const apply = useMutation({
    mutationFn: () =>
      applyEffect(client, playerId, {
        effectId,
        target: target.trim() || null,
        reason: reason.trim(),
      }),
    onSuccess: done,
    onError: (e) => setProblem(errorText(e)),
  });
  const end = useMutation({
    mutationFn: (id: number) => endEffect(client, id, reason.trim()),
    onSuccess: done,
    onError: (e) => setProblem(errorText(e)),
  });
  const why = useMutation({
    mutationFn: () => explainEffect(client, playerId, member, modKey),
    onSuccess: (x) => {
      setExplain(x);
      setProblem(null);
    },
    onError: (e) => setProblem(errorText(e)),
  });

  // Effects the API can store (not derived), from the local draft: the API applies from the *published* content.
  const applicable = (draft?.bundle.statusEffects ?? [])
    .filter((e) => e != null && effectAsParsed(e).duration?.kind !== 'derived')
    .map((e) => e!.id ?? '');

  const d = q.data;
  const members = d?.active ? Object.keys(d.active.members) : [];
  const instances = [...(d?.instances ?? [])].sort(
    (a, b) =>
      Number(a.endedAt != null) - Number(b.endedAt != null) ||
      b.startedAt.localeCompare(a.startedAt),
  );
  const noReason = reason.trim() === '';

  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <h2 className="font-semibold">Effects</h2>
      {q.isPending && <p className="text-sm">Loading…</p>}
      {q.error && (
        <p className="text-sm text-blam">
          {errorText(q.error).includes('404')
            ? 'Not available yet (an older API).'
            : errorText(q.error)}
        </p>
      )}
      {d && (
        <>
          <p className="text-xs text-bruise">
            Run {d.runId ?? 'none'} {d.runStatus ? `(${statusWord(d.runStatus)})` : ''}
          </p>

          <h3 className="mt-2 text-sm font-semibold">Active now</h3>
          {!d.active ? (
            <p className="text-sm">No live run.</p>
          ) : (
            <ul className="text-xs">
              {[
                ...members.map((m) => [m, d.active!.members[m]] as const),
                ['base', d.active.base] as const,
              ].map(([m, list]) => (
                <li key={m}>
                  <span className="font-mono font-semibold">{m}</span>:{' '}
                  {list?.length
                    ? list
                        .map(
                          (a) =>
                            `${a.tape || a.name}${(a.stacks ?? 1) > 1 ? ` x${a.stacks}` : ''} (${a.durationKind}${
                              a.untilMs ? `, until ${new Date(a.untilMs).toLocaleString()}` : ''
                            })`,
                        )
                        .join(' · ')
                    : 'none'}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="text-xs font-semibold">
              Reason (apply and end)
              <input
                aria-label="Reason"
                className={`${input} ml-1 w-64`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          </div>

          <h3 className="mt-3 text-sm font-semibold">Instances</h3>
          {instances.length === 0 ? (
            <p className="text-sm">No effect instances this run.</p>
          ) : (
            <table className="mt-1 w-full text-xs">
              <thead className="text-bruise">
                <tr>
                  <th className={th}>Effect</th>
                  <th className={th}>Scope</th>
                  <th className={th}>Stacks</th>
                  <th className={th}>Source</th>
                  <th className={th}>Started</th>
                  <th className={th}>Expires / ended</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {instances.map((r) => (
                  <tr key={r.id} className="border-t border-newsprint align-top">
                    <td className={td}>
                      <span className="font-semibold">{r.name}</span>{' '}
                      <span className="font-mono text-bruise">{r.effectId}</span>
                    </td>
                    <td className={`${td} font-mono`}>
                      {r.scope} {r.scopeId.slice(0, 8)}
                    </td>
                    <td className={td}>{r.stacks}</td>
                    <td className={`${td} font-mono`}>
                      {r.sourceKind} {r.durationKind === 'zone' ? '(zone)' : ''}
                    </td>
                    <td className={td}>{when(r.startedAt)}</td>
                    <td className={td}>
                      {r.endedAt
                        ? `ended ${when(r.endedAt)} (${r.endedReason ?? ''})`
                        : when(r.expiresAt) || r.durationKind}
                    </td>
                    <td className={td}>
                      {!r.endedAt && (
                        <button
                          type="button"
                          className={btn}
                          disabled={noReason || end.isPending}
                          title={noReason ? 'Give a reason first.' : undefined}
                          onClick={() => {
                            if (confirm(`End ${r.name} (${r.effectId}) now?`)) end.mutate(r.id);
                          }}
                        >
                          End
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 className="mt-3 text-sm font-semibold">Zone presence</h3>
          {d.presence.length === 0 ? (
            <p className="text-sm">Not in any zone.</p>
          ) : (
            <ul className="text-xs">
              {d.presence.map((p) => (
                <li key={p.zoneId}>
                  <span className="font-semibold">{p.kind}</span>{' '}
                  {p.contentId ?? p.baseId ?? p.zoneId.slice(0, 8)}: {p.live ? 'live' : 'stale'},
                  entered {when(p.enteredAt)}, confirmed {when(p.confirmedAt)}, stale at{' '}
                  {when(p.staleAt)}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-bruise">
            Zones:{' '}
            {d.zones
              .map(
                (z) =>
                  `${z.kind} ${z.contentId ?? z.baseId?.slice(0, 8) ?? ''}${z.active ? '' : ' (inactive)'}`,
              )
              .join(' · ') || 'none'}
          </p>

          <h3 className="mt-3 text-sm font-semibold">Why is it this value?</h3>
          <div className="flex flex-wrap items-end gap-2">
            <select
              aria-label="Member"
              className={input}
              value={member}
              onChange={(e) => setMember(e.target.value)}
            >
              {[...new Set(['player', 'base', ...members])].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              aria-label="Modifier key"
              className={input}
              value={modKey}
              onChange={(e) => setModKey(e.target.value)}
            >
              {MODIFIER_REGISTRY.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.key}
                </option>
              ))}
            </select>
            <button
              type="button"
              className={btn}
              disabled={why.isPending || !d.runId}
              onClick={() => why.mutate()}
            >
              Explain
            </button>
          </div>
          {explain && (
            <div
              className="mt-1 rounded border border-newsprint p-2 font-mono text-xs"
              data-testid="explain"
            >
              <p>
                {explain.key} for {explain.target}: base {explain.base} → <b>{explain.result}</b>
              </p>
              <ol className="list-decimal pl-5">
                {(explain.steps ?? []).map((s, i) => (
                  <li key={i}>{stepText(s)}</li>
                ))}
              </ol>
            </div>
          )}

          <h3 className="mt-3 text-sm font-semibold">Apply an effect</h3>
          <div className="flex flex-wrap items-end gap-2">
            <input
              aria-label="Effect id"
              list="apply-effect-ids"
              className={input}
              placeholder="effect id"
              value={effectId}
              onChange={(e) => setEffectId(e.target.value)}
            />
            <datalist id="apply-effect-ids">
              {applicable.map((id) => (
                <option key={id} value={id} />
              ))}
            </datalist>
            <input
              aria-label="Target"
              className={input}
              placeholder="target: blank, player, base or a survivor id"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
            <button
              type="button"
              className={btn}
              disabled={noReason || effectId.trim() === '' || apply.isPending}
              onClick={() => {
                if (
                  confirm(
                    `Apply ${effectId}${target ? ` to ${target}` : ''}? It uses the published content.`,
                  )
                )
                  apply.mutate();
              }}
            >
              Apply
            </button>
          </div>
        </>
      )}
      {problem && (
        <p className="mt-2 text-sm text-blam" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}
